/**
 * LeadStageStrip — the horizontal ‹Prev / Next› rail above the shared lead
 * detail drawer (AD-046 §7). It reads `nextStates(lead.state)` to enable the
 * legal forward/back hops and the park/reject off-ramp, and calls back
 * through the SAME `onState`→`transitionLead`→`updateLead` path the Existing
 * screen uses. No parallel write path; an illegal hop is simply not offered.
 *
 * For a Fiverr-pipeline lead it switches to the `fstage` model: it renders
 * the fstage nodes and advances via `nextFstages()` (a snapshot-field write,
 * persisted by the parent). `converted` is NEVER offered here — conversion
 * needs the full Convert flow in `<LeadDetail>` (client+project creation),
 * so the strip stops at `quoted` and the record's Convert button takes over.
 */
import React from 'react';
import {
  LEAD_SPINE,
  LEAD_STATE_LABEL,
  nextStates,
  type Lead,
  type LeadState,
} from '../lib/leads';
import {
  ALL_FSTAGES,
  FSTAGE_LABEL,
  fstageOf,
  isFiverr,
  nextFstages,
  type FStage,
} from '../lib/leadPipeline';

const nodeBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 10.5,
  fontWeight: 700,
  padding: '4px 10px',
  borderRadius: 'var(--r-pill, 999px)',
  border: '1px solid var(--rule, #E5E7EB)',
  background: 'var(--inset, #F6F7F9)',
  color: 'var(--ink-mute, #6B7280)',
  whiteSpace: 'nowrap',
};

function Btn({
  label,
  onClick,
  disabled,
  tone,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'accent' | 'warn';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...nodeBase,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        background:
          tone === 'warn'
            ? 'var(--warn-wash, #FFFBEB)'
            : 'var(--accent-wash, #E9F7EF)',
        color:
          tone === 'warn'
            ? 'var(--warn, #B45309)'
            : 'var(--accent-deep, #075E54)',
        borderColor:
          tone === 'warn' ? '#F0DDB0' : 'var(--accent, #008069)',
      }}
    >
      {label}
    </button>
  );
}

export default function LeadStageStrip({
  lead,
  onState,
  onFstage,
  disabled,
}: {
  lead: Lead;
  /** Advance/retreat the live pipeline state (legal hops only). */
  onState: (to: LeadState) => void;
  /** Advance the Fiverr fstage (forward-only). */
  onFstage: (to: FStage) => void;
  /** true → read-only (no commit rights). */
  disabled?: boolean;
}) {
  const fiverr = isFiverr(lead);

  /* ── Fiverr pipeline strip ── */
  if (fiverr) {
    const cur: FStage = fstageOf(lead);
    const curIdx = ALL_FSTAGES.indexOf(cur);
    const fwd = nextFstages(cur);
    const nextF = fwd[0] ?? null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
        {ALL_FSTAGES.map((s, i) => {
          const state =
            i < curIdx ? 'done' : i === curIdx ? 'cur' : 'todo';
          return (
            <span
              key={s}
              style={{
                ...nodeBase,
                ...(state === 'done'
                  ? { background: 'var(--good-wash, #F0FDF4)', color: 'var(--good, #15803D)', borderColor: '#BBE6C6' }
                  : state === 'cur'
                    ? { background: 'var(--accent-wash, #E9F7EF)', color: 'var(--accent-deep, #075E54)', borderColor: 'var(--accent, #008069)' }
                    : {}),
              }}
            >
              {state === 'done' ? '✓ ' : state === 'cur' ? '● ' : ''}
              {FSTAGE_LABEL[s]}
            </span>
          );
        })}
        {nextF && (
          <Btn
            label={`Advance → ${FSTAGE_LABEL[nextF]}`}
            onClick={() => onFstage(nextF)}
            disabled={disabled}
          />
        )}
      </div>
    );
  }

  /* ── Standard pipeline strip ── */
  const legal = nextStates(lead.state);
  const spineIdx = LEAD_SPINE.indexOf(lead.state);
  // Prev = the spine state immediately before current, IF that hop is legal.
  const prevState: LeadState | null =
    spineIdx > 0 ? LEAD_SPINE[spineIdx - 1] : null;
  const prevLegal = prevState != null && legal.includes(prevState);
  // Next = the next spine state, IF legal AND not 'converted' (Convert is a
  // dedicated flow in the record below, not a bare transition here).
  const nextState: LeadState | null =
    spineIdx >= 0 && spineIdx < LEAD_SPINE.length - 1
      ? LEAD_SPINE[spineIdx + 1]
      : null;
  const nextLegal =
    nextState != null && nextState !== 'converted' && legal.includes(nextState);
  const canPark = legal.includes('parked');
  const canReject = legal.includes('rejected');

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
      <Btn
        label="‹ Prev"
        onClick={() => prevState && onState(prevState)}
        disabled={disabled || !prevLegal}
      />
      {LEAD_SPINE.map((s, i) => {
        const state = i < spineIdx ? 'done' : i === spineIdx ? 'cur' : 'todo';
        return (
          <span
            key={s}
            style={{
              ...nodeBase,
              ...(state === 'done'
                ? { background: 'var(--good-wash, #F0FDF4)', color: 'var(--good, #15803D)', borderColor: '#BBE6C6' }
                : state === 'cur'
                  ? { background: 'var(--accent-wash, #E9F7EF)', color: 'var(--accent-deep, #075E54)', borderColor: 'var(--accent, #008069)' }
                  : {}),
            }}
          >
            {state === 'done' ? '✓ ' : state === 'cur' ? '● ' : ''}
            {LEAD_STATE_LABEL[s]}
          </span>
        );
      })}
      <Btn
        label={nextState && nextState !== 'converted' ? `Next › ${LEAD_STATE_LABEL[nextState]}` : 'Next ›'}
        onClick={() => nextState && onState(nextState)}
        disabled={disabled || !nextLegal}
      />
      {canPark && (
        <Btn label="Park" tone="warn" onClick={() => onState('parked')} disabled={disabled} />
      )}
      {canReject && (
        <Btn label="Reject" tone="warn" onClick={() => onState('rejected')} disabled={disabled} />
      )}
      {(lead.state === 'parked' || lead.state === 'rejected') && (
        <span style={{ ...nodeBase, background: 'var(--warn-wash, #FFFBEB)', color: 'var(--warn, #B45309)', borderColor: '#F0DDB0' }}>
          Off-ramp: {LEAD_STATE_LABEL[lead.state]}
        </span>
      )}
    </div>
  );
}
